import { useEffect, useState } from "react";
import { X, ZoomIn, RotateCw, Droplets, Heart, Check, ShieldCheck, AlertTriangle, ArrowLeft } from "lucide-react";
import { Logo } from "../components/Logo";

const STEPS = [
  {
    n: "01",
    title: "Insert the cotton",
    desc: "Compress, twist and gently insert one sterile cotton strip into the Mi Binky chamber using your fingertip.",
    icon: RotateCw,
  },
  {
    n: "02",
    title: "Prepare the cotton",
    desc: "Apply a few drops of your chosen compatible comfort liquid onto the cotton until lightly moistened — through both holes, as shown in the pack instructions.",
    icon: Droplets,
  },
  {
    n: "03",
    title: "Use Mi Binky",
    desc: "The cotton stays contained within the protected chamber while gentle vapours release naturally through the front ventilation holes as your child uses their Binky.",
    icon: Heart,
  },
];

const RULES = [
  "Adult use only when preparing the comfort chamber",
  "Always supervise babies and toddlers while using any pacifier",
  "Use only compatible BINKY Comfort accessories",
  "Replace worn or damaged products immediately",
];

const CARD_ALT = "The official Mi Binky instruction card supplied in every pack.";

export default function VideoGuide() {
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => e.key === "Escape" && setLightbox(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <div className="min-h-screen bg-[#FBFDFF] font-body text-slate-900 antialiased" data-testid="video-guide-page">
      <header className="sticky top-0 z-40 border-b border-binky-blue/10 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-lg items-center justify-between px-5">
          <Logo />
          <span className="rounded-full bg-binky-pinklight px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-binky-pink" data-testid="guide-badge">
            Video Guide
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-5 pb-20">
        <div className="mt-4 overflow-hidden rounded-[24px] border-4 border-white bg-binky-navy shadow-2xl shadow-binky-blue/25">
          <video
            data-testid="guide-video"
            src="/assets/binky-demo.mp4"
            poster="/assets/binky-demo-poster.jpg"
            controls
            playsInline
            preload="auto"
            className="mx-auto max-h-[56vh] w-full max-w-[340px] bg-binky-navy object-contain"
          />
        </div>

        <div className="mt-6 text-center">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900" data-testid="guide-heading">
            How to Use <span className="text-binky-pink">Mi</span> <span className="text-binky-blue">Binky</span>
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Watch the short video above, then follow the three steps below — everything you need is
            in your pack.
          </p>
        </div>

        <div className="mt-10">
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900" data-testid="guide-steps-heading">
            Three gentle steps
          </h2>
          <div className="mt-6 space-y-4">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.n}
                  data-testid={`guide-step-${s.n}`}
                  className="flex gap-4 rounded-3xl border border-slate-100 bg-white p-5 shadow-[0_16px_40px_-20px_rgba(27,79,160,0.3)]"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-binky-pink font-display text-sm font-extrabold text-white">
                    {s.n}
                  </span>
                  <div>
                    <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
                      {s.title}
                      <Icon className="h-4 w-4 text-binky-blue" />
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-start gap-3 rounded-3xl bg-binky-navy p-5 text-white" data-testid="guide-replacing-note">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-binky-green/25 text-binky-green">
              <Check className="h-4 w-4" />
            </span>
            <p className="text-sm leading-relaxed text-white/85">
              <strong className="font-bold text-white">Replacing the cotton:</strong> remove the
              used cotton with the supplied precision tweezers — never by hand — and fit a new
              sterile cotton strip whenever required.
            </p>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
            Your instruction card
          </h2>
          <p className="mt-2 text-sm text-slate-600">The official card supplied in every pack — tap to view full-size.</p>
          <button
            data-testid="guide-card-button"
            onClick={() => setLightbox(true)}
            className="group relative mt-5 block w-full cursor-zoom-in overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-xl shadow-binky-blue/10"
            aria-label="View the Mi Binky instruction card full-size"
          >
            <img
              src="/assets/binky-instructions.png"
              alt={CARD_ALT}
              loading="lazy"
              data-testid="guide-card-image"
              className="h-auto w-full object-contain"
            />
            <span className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-full bg-binky-navy/85 px-3.5 py-1.5 text-[11px] font-bold text-white backdrop-blur">
              <ZoomIn className="h-3.5 w-3.5" />
              Tap to enlarge
            </span>
          </button>
        </div>

        <div className="mt-12 rounded-3xl border border-binky-blue/10 bg-sky-50/70 p-6" data-testid="guide-safety">
          <h2 className="flex items-center gap-2 font-display text-xl font-extrabold text-slate-900">
            <ShieldCheck className="h-5 w-5 text-binky-green" />
            Using Mi Binky safely
          </h2>
          <ul className="mt-4 space-y-3">
            {RULES.map((r) => (
              <li key={r} className="flex items-start gap-3 text-sm font-semibold text-slate-700">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-binky-green/15 text-binky-green">
                  <Check className="h-3 w-3" />
                </span>
                {r}
              </li>
            ))}
          </ul>
          <div className="mt-5 flex items-start gap-3 rounded-2xl border border-binky-pink/25 bg-binky-pinklight/60 px-4 py-3.5" data-testid="guide-conformity-note">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-binky-pink" />
            <p className="text-xs leading-relaxed text-slate-600">
              The Mi Binky pacifier design is undergoing independent conformity testing. Final
              product instructions and claims will reflect the completed testing.
            </p>
          </div>
        </div>

        <a
          href="/"
          data-testid="guide-home-button"
          className="mt-10 flex w-full items-center justify-center gap-2 rounded-full bg-binky-pink px-8 py-4 text-sm font-bold text-white shadow-lg shadow-binky-pink/30 transition-transform duration-300 hover:scale-[1.02] active:scale-95"
        >
          <ArrowLeft className="h-4 w-4" />
          Visit BINKY.LIFE
        </a>

        <p className="mt-10 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400" data-testid="guide-footer">
          © 2026 BINKY.LIFE · Designed in Britain · Patent Pending
        </p>
        <div className="mt-2 text-center">
          <button
            data-testid="guide-cookie-settings"
            onClick={() => window.dispatchEvent(new Event("mibinky:cookie-settings"))}
            className="text-[11px] font-semibold text-slate-400 underline decoration-binky-pink/30 underline-offset-4 transition-colors hover:text-binky-pink"
          >
            Cookie Settings
          </button>
        </div>
      </main>

      {lightbox && (
        <div
          data-testid="guide-card-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-binky-navy/90 p-4 backdrop-blur-md"
          onClick={() => setLightbox(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Mi Binky instruction card — full-size view"
        >
          <button
            data-testid="guide-card-lightbox-close"
            onClick={() => setLightbox(false)}
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-binky-pink"
            aria-label="Close full-size instruction card"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src="/assets/binky-instructions.png"
            alt={CARD_ALT}
            className="max-h-[92vh] max-w-[94vw] rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
